import React, { useState, useEffect, useRef, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  TextField,
  Button,
  Typography,
  Grid,
  IconButton,
  Box,
  Autocomplete,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import DimensionsAndWeightInputWithUnit from "../../components/dimension-and-weight-unit";
import { productService } from "../../services/product-service";
import { imageService } from "../../services/image-service";
import { currencyTypes } from "../../utils/currency";
import config from "../../config";
import AutocompleteWithPagination from "../../components/autocomplete-with-pagination";
import buildQuery from "../../utils/query-builder"; // Assuming this utility exists
import { randomUid } from "../../utils/randomUid"; // Assuming this utility exists

const defaultImage = "/product.png";

const ManageProduct = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);

  const initialData = {
    name: "",
    url: "",
    short_description: "",
    pga_flag: "",
    description: "",
    images: [],
    status: "",
    product_type: "",
    variants: [
      {
        title: "",
        price: "",
        weight: "",
        weight_unit: "",
        height: "",
        height_unit: "",
        width: "",
        width_unit: "",
        length: "",
        length_unit: "",
        sku: "",
        hs_code: "",
        country_of_origin: "",
        hts_code: "",
        isBundle: false,
        isMaster: false,
        uid: randomUid(),
        parent: null,
        subSkus: [],
      },
    ],
    currency: "",
    vendor: null,
  };

  const [productData, setProductData] = useState(initialData);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [coverImageIndex, setCoverImageIndex] = useState(-1);
  const [imageUrls, setImageUrls] = useState([]);
  const [editingImageIndex, setEditingImageIndex] = useState(null);
  const [imageUrl, setImageUrl] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [featureImages, setFeatureImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedVariants, setExpandedVariants] = useState({ 0: true });
  const [hsCodeErrors, setHsCodeErrors] = useState({});
  const [validated, setValidated] = useState(false);
  const [skuErrors, setSkuErrors] = useState([]);
  const productId = location.state?.product;

  const fetchImage = (image) => {
    if (image && image.url) {
      return image.url;
    }
    if (image && image.image) {
      return `${config.baseURL}/fetch-file/${image.image}`;
    }
    return defaultImage;
  };

  const fetchProductDetails = async (productId) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await productService.fetchById(productId);
      let product = JSON.parse(JSON.stringify(response));
      setProductData(product);
      setSelectedProduct(product);

      let parentVariants = product.variants.filter(
        (variant) => !variant.parent
      );
      setProductData((prev) => ({
        ...prev,
        variants: parentVariants.map((variant) => ({
          ...variant,
          subSkus: product.variants
            .filter(
              (subVariant) =>
                subVariant.parent && subVariant.parent === variant.uid
            )
            .map((subVariant) => ({
              ...subVariant,
              sku: variant.isBundle ? { sku: subVariant.sku } : subVariant.sku,
            })),
        })),
      }));

      const fetchedImages = product.images.map(fetchImage);
      setSelectedImages(
        product.images.map((img) => ({
          image: img.image,
          feature_image: img.feature_image,
          url: img.url,
        }))
      );
      setImageUrls(fetchedImages);
      const coverIndex = product.images.findIndex((img) => img.feature_image);
      setCoverImageIndex(coverIndex);
      setFeatureImages(product.images.map((img) => img.feature_image));
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch product details.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (productId) {
      fetchProductDetails(productId);
    }
  }, [productId]);

  useEffect(() => {
    setLoadingSuppliers(true);
    setTimeout(() => {
      setSuppliers([
        { label: "Supplier 1", id: 1 },
        { label: "Supplier 2", id: 2 },
        { label: "Supplier 3", id: 3 },
      ]);
      setLoadingSuppliers(false);
    }, 1000);
  }, []);

  const handleInputChange = (field, value) => {
    setProductData((prev) => ({ ...prev, [field]: value }));
  };

  const handleVariantChange = (index, field, value) => {
    const updatedVariants = [...productData.variants];
    if (updatedVariants[index]) {
      updatedVariants[index][field] = value;
      setProductData((prev) => ({ ...prev, variants: updatedVariants }));
    }
    if (field === "hs_code") {
      const hsCodeLength = value.toString()?.length;
      const errorMessage =
        hsCodeLength < 8 || hsCodeLength > 12
          ? "HS Code length must be between 8 and 12 characters."
          : "";
      setHsCodeErrors((prevErrors) => ({
        ...prevErrors,
        [index]: errorMessage,
      }));
    }
    if (field === "sku") {
      const trimmed = value.trim();
      const regex = /^[A-Za-z][A-Za-z0-9_-]{3,29}$/;

      const duplicate = productData.variants.some(
        (v, i) => i !== index && v.sku === trimmed
      );

      let err = "";

      if (!regex.test(trimmed))
        err = "4-30 characters. Start with letter. Only A-Z, 0-9, _ or -";
      else if (duplicate) err = "SKU must be unique";

      setSkuErrors((prev) => ({ ...prev, [index]: err }));
    }
  };

  const handleAddVariant = () => {
    const newVariant = {
      ...initialData.variants[0],
      uid: randomUid(),
    };
    setProductData((prev) => ({
      ...prev,
      variants: [...prev.variants, newVariant],
    }));
    setExpandedVariants((prev) => ({
      ...prev,
      [prev.variants?.length]: true,
    }));
  };

  const handleRemoveVariant = (index) => {
    const updatedVariants = [...productData.variants];
    updatedVariants.splice(index, 1);
    setProductData((prev) => ({ ...prev, variants: updatedVariants }));
    setExpandedVariants((prev) => {
      const newExpanded = { ...prev };
      delete newExpanded[index];
      return newExpanded;
    });
  };

  const handleToggleExpand = (index) => {
    setExpandedVariants((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleBundleToggle = (variantIndex, isChecked, key = "isBundle") => {
    const updatedVariants = [...productData.variants];
    updatedVariants[variantIndex][key] = isChecked;

    if (
      isChecked &&
      updatedVariants[variantIndex].subSkus &&
      updatedVariants[variantIndex].subSkus.length === 0
    ) {
      updatedVariants[variantIndex].subSkus = [
        {
          title: "",
          price: "",
          sku: "",
          hs_code: "",
          hts_code: "",
          country_of_origin: "",
          height: "",
          width: "",
          length: "",
          weight: "",
          height_unit: "",
          weight_unit: "",
          product: null,
          skuSelection: null,
          uid: randomUid(),
          parent: updatedVariants[variantIndex].uid,
          child: key === "isMaster",
        },
      ];
    } else {
      updatedVariants[variantIndex].subSkus = updatedVariants[
        variantIndex
      ].subSkus.map((x) => ({ ...x, child: isChecked && key === "isMaster" }));
    }

    setProductData((prev) => ({ ...prev, variants: updatedVariants }));
  };

  const handleAddSubSku = (variantIndex, master) => {
    const newVariants = [...productData.variants];
    newVariants[variantIndex].subSkus.push({
      title: "",
      price: "",
      sku: "",
      hs_code: "",
      hts_code: "",
      country_of_origin: "",
      height: "",
      width: "",
      length: "",
      weight: "",
      height_unit: "",
      weight_unit: "",
      product: null,
      skuSelection: null,
      uid: randomUid(),
      parent: newVariants[variantIndex].uid,
      child: master,
    });
    setProductData((prev) => ({ ...prev, variants: newVariants }));
  };

  const handleRemoveSubSku = (variantIndex, subIndex) => {
    const newVariants = [...productData.variants];
    newVariants[variantIndex].subSkus.splice(subIndex, 1);
    setProductData((prev) => ({ ...prev, variants: newVariants }));
  };

  const handleSubSkuChange = (variantIndex, subIndex, key, value) => {
    const newVariants = [...productData.variants];
    newVariants[variantIndex].subSkus[subIndex][key] = value;
    setProductData((prev) => ({ ...prev, variants: newVariants }));
  };

  const handleProductUpdate = (variantIndex, subIndex, product) => {
    const newVariants = [...productData.variants];
    newVariants[variantIndex].subSkus[subIndex].product = product;
    newVariants[variantIndex].subSkus[subIndex].title = "";
    newVariants[variantIndex].subSkus[subIndex].price = "";
    newVariants[variantIndex].subSkus[subIndex].sku = null;
    newVariants[variantIndex].subSkus[subIndex].hs_code = "";
    newVariants[variantIndex].subSkus[subIndex].hts_code = "";
    newVariants[variantIndex].subSkus[subIndex].country_of_origin = "";
    newVariants[variantIndex].subSkus[subIndex].height = "";
    newVariants[variantIndex].subSkus[subIndex].width = "";
    newVariants[variantIndex].subSkus[subIndex].length = "";
    newVariants[variantIndex].subSkus[subIndex].weight = "";
    newVariants[variantIndex].subSkus[subIndex].height_unit = "";
    newVariants[variantIndex].subSkus[subIndex].weight_unit = "";
    setProductData((prev) => ({ ...prev, variants: newVariants }));
  };

  const updateSubSku = (variantIndex, subIndex, subSku, selectedSku) => {
    const product = subSku.product;
    if (product?.variants?.length > 0) {
      const matchedVariant = product.variants.find(
        (v) => v.sku === selectedSku?.sku
      );
      const autoFilled = {
        title: matchedVariant?.title || "",
        price: matchedVariant?.price || "",
        weight: matchedVariant?.weight || "",
        weight_unit: matchedVariant?.weight_unit || "",
        height: matchedVariant?.height || "",
        height_unit: matchedVariant?.height_unit || "",
        width: matchedVariant?.width || "",
        width_unit: matchedVariant?.width_unit || "",
        length: matchedVariant?.length || "",
        length_unit: matchedVariant?.length_unit || "",
        hs_code: matchedVariant?.hs_code || "",
        hts_code: matchedVariant?.hts_code || "",
        country_of_origin: matchedVariant?.country_of_origin || "",
        sku: selectedSku || "",
      };

      setProductData((prev) => ({
        ...prev,
        variants: prev.variants.map((variant, index) => {
          if (index === variantIndex) {
            return {
              ...variant,
              subSkus: variant.subSkus.map((sub, si) => {
                if (si === subIndex) {
                  return { ...sub, ...autoFilled };
                }
                return sub;
              }),
            };
          }
          return variant;
        }),
      }));
    }
  };

  const validateForm = () => {
    if (
      !productData.name ||
      !productData.currency ||
      !productData.status ||
      !productData.vendor
    ) {
      return false;
    }

    const skuRegex = /^[A-Za-z][A-Za-z0-9_-]{3,29}$/;

    for (let i = 0; i < productData.variants.length; i++) {
      const v = productData.variants[i];
      const skuStr = (v.sku?.sku || v.sku || "").trim();

      if (!v.title || !v.price || !skuStr) return false;
      if (!skuRegex.test(skuStr)) return false;
    }

    return true;
  };

  useEffect(() => {
    setValidated(validateForm());
  }, [productData]);

  const flattenProductVariants = (variants) => {
    const flatVariants = [];
    for (const variant of variants || []) {
      const { subSkus, ...mainVariant } = variant;
      flatVariants.push(mainVariant);

      if (Array.isArray(subSkus)) {
        for (const subSku of subSkus) {
          flatVariants.push({
            title: subSku.title,
            sku: subSku.sku?.sku || subSku.sku || "",
            price: subSku.price,
            hs_code: subSku.hs_code,
            hts_code: subSku.hts_code,
            country_of_origin: subSku.country_of_origin,
            height: subSku.height,
            width: subSku.width,
            length: subSku.length,
            weight: subSku.weight,
            height_unit: subSku.height_unit,
            width_unit: subSku.width_unit,
            length_unit: subSku.length_unit,
            weight_unit: subSku.weight_unit,
            product: subSku.product?._id,
            parent: subSku.parent,
            uid: subSku.uid,
            child: subSku.child,
          });
        }
      }
    }
    return flatVariants;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.warning("Please fill all mandatory fields.");
      return;
    }

    try {
      setIsLoading(true);
      const imageField =
        selectedImages.length > 0
          ? selectedImages.map((image) => ({
              image: image.image,
              feature_image: image.feature_image,
              url: image.url,
            }))
          : selectedProduct?.images || [];

      if (location.state?.isEditing) {
        await productService.edit(
          {
            ...productData,
            images: imageField,
            variants: flattenProductVariants(productData.variants),
          },
          productId
        );
        toast.success("Product updated successfully!");
      } else {
        await productService.create({
          ...productData,
          images: imageField,
          variants: flattenProductVariants(productData.variants),
          // client_id: localStorage.getItem("user")?._id,
        });
        toast.success("Product created successfully!");
      }

      navigate("/products");
    } catch (error) {
      console.error("Error saving product:", error);
      toast.error("Failed to save product.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/products");
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const file = files[0];
      try {
        const imageData = await imageService.upload(file);
        const imageUrl = URL.createObjectURL(file);
        setSelectedImages((prevImages) => {
          const newImages = [...prevImages];
          if (editingImageIndex !== null) {
            newImages[editingImageIndex] = {
              image: imageData.key,
              feature_image: editingImageIndex === coverImageIndex,
            };
          } else {
            newImages.push({
              image: imageData.key,
              feature_image: false,
            });
          }
          setImageUrl(newImages);
          return newImages;
        });
        setImageUrls((prevUrls) => {
          const newUrls = [...prevUrls];
          if (editingImageIndex !== null) {
            newUrls[editingImageIndex] = imageUrl;
          } else {
            newUrls.push(imageUrl);
          }
          return newUrls;
        });
        setEditingImageIndex(null);
      } catch (error) {
        console.error("Image upload failed:", error);
        toast.error("Image upload failed.");
      } finally {
        e.target.value = null;
      }
    }
  };

  const handleChooseImage = (index = null) => {
    setEditingImageIndex(index);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      fileInputRef.current.click();
    }
  };

  const handleCheckCoverImage = (index) => {
    setCoverImageIndex(index === coverImageIndex ? -1 : index);
    setSelectedImages((prevImages) => {
      const updatedImages = [...prevImages];
      updatedImages.forEach((image, i) => {
        image.feature_image = i === index;
      });
      return updatedImages;
    });
  };

  const removeSelectedImage = (indexToRemove) => {
    setSelectedImages((prevImages) =>
      prevImages.filter((_, index) => index !== indexToRemove)
    );
    setImageUrls((prevUrls) =>
      prevUrls.filter((_, index) => index !== indexToRemove)
    );

    if (coverImageIndex === indexToRemove) {
      setCoverImageIndex(-1);
    } else if (coverImageIndex > indexToRemove) {
      setCoverImageIndex((prev) => prev - 1);
    }
  };

  const onChangeDimensionAndWight = (index, field, value) => {
    if (
      ["weight", "height", "width", "length", "weight_unit"].includes(field)
    ) {
      handleVariantChange(index, field, value);
    } else if (field === "dimension_unit") {
      const updatedVariants = [...productData.variants];
      if (updatedVariants[index]) {
        updatedVariants[index]["height_unit"] = value;
        updatedVariants[index]["width_unit"] = value;
        updatedVariants[index]["length_unit"] = value;
        setProductData({ ...productData, variants: updatedVariants });
      }
    }
  };

  const onChangeSubDimensionAndWeight = (
    variantIndex,
    subIndex,
    field,
    value
  ) => {
    const updatedVariants = [...productData.variants];
    if (
      !updatedVariants[variantIndex] ||
      !updatedVariants[variantIndex].subSkus[subIndex]
    )
      return;

    const subSku = updatedVariants[variantIndex].subSkus[subIndex];
    if (
      ["weight", "weight_unit", "height", "width", "length"].includes(field)
    ) {
      subSku[field] = value;
    } else if (field === "dimension_unit") {
      subSku["height_unit"] = value;
      subSku["width_unit"] = value;
      subSku["length_unit"] = value;
    }
    setProductData({ ...productData, variants: updatedVariants });
  };

  return (
    <Box>
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
            color: "white",
          }}
        >
          <CircularProgress color="inherit" />
        </Box>
      )}
      <Typography sx={{ padding: 1 }} variant="h5">
        {productId ? "Edit Product" : "Add Product"}
      </Typography>
      <Box
        sx={{
          maxHeight: "calc(100vh - 150px)",
          overflowY: "auto",
          scrollBehavior: "smooth",
          padding: 1,
          "&::-webkit-scrollbar": {
            width: "7px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#D9D9D9",
            borderRadius: "14px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#273894",
            borderRadius: "6px",
            "&:hover": {
              backgroundColor: "#555",
            },
          },
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              sx={{ backgroundColor: "#FFFFFF" }}
              size="small"
              fullWidth
              label="Name"
              value={productData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              sx={{ backgroundColor: "#FFFFFF" }}
              size="small"
              fullWidth
              label="Category"
              value={productData.product_type}
              onChange={(e) =>
                handleInputChange("product_type", e.target.value)
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              sx={{ backgroundColor: "#FFFFFF" }}
              size="small"
              fullWidth
              label="Short Description"
              value={productData.short_description}
              onChange={(e) =>
                handleInputChange("short_description", e.target.value)
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              sx={{ backgroundColor: "#FFFFFF" }}
              size="small"
              fullWidth
              label="PGA Flag"
              value={productData.pga_flag}
              onChange={(e) => handleInputChange("pga_flag", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={12}>
            <TextField
              sx={{ backgroundColor: "#FFFFFF" }}
              size="small"
              fullWidth
              label="URL"
              value={productData.url}
              onChange={(e) => handleInputChange("url", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <div style={{ marginBottom: "30px" }}>
              <ReactQuill
                theme="snow"
                value={productData.description}
                onChange={(e) => handleInputChange("description", e)}
                placeholder="Enter product description"
                style={{
                  marginTop: 5,
                  backgroundColor: "#FFFFFF",
                  height: "120px",
                }}
              />
              <style>
                {`
                  .ql-container {
                    background-color: #FFFFFF !important;
                    height: 100%;
                  }
                  .ql-editor {
                    min-height: 100%;
                    background-color: #FFFFFF !important;
                    padding: 10px;
                    border-radius: 8px;
                  }
                  .ql-tooltip {
                    position: absolute !important;
                    top: 50% !important;
                    left: 50% !important;
                    transform: translate(-50%, -50%) !important;
                    max-width: 90vw;
                    width: auto;
                    z-index: 1000;
                  }
                  .ql-tooltip a {
                    cursor: pointer;
                    text-decoration: underline;
                  }
                `}
              </style>
            </div>
          </Grid>
          <Grid item xs={12} sx={{ marginTop: 2 }} sm={12}>
            <Box
              sx={{
                width: "97%",
                border: 1,
                borderColor: "#D1D1D1",
                borderRadius: 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "150px",
                p: 2,
                backgroundColor: "#FFFFFF",
                marginTop: 1,
              }}
            >
              <input
                id="productImageInput"
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleImageChange}
              />
              {selectedImages.length > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "97%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "flex-start",
                      gap: 2,
                      mt: 2,
                      maxHeight: 250,
                      overflowY: "auto",
                      border: "1px solid #000",
                      borderRadius: 1,
                      p: 2,
                    }}
                  >
                    {selectedImages?.map((image, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          p: 1,
                          border: 1,
                          borderRadius: 1,
                          maxWidth: { xs: "100%", sm: "auto" },
                        }}
                      >
                        <img
                          src={imageUrls[index]}
                          alt={`image ${index}`}
                          style={{
                            maxWidth: 100,
                            maxHeight: 100,
                          }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            alignItems: "center",
                          }}
                        >
                          <IconButton onClick={() => handleChooseImage(index)}>
                            <EditIcon color="primary" />
                          </IconButton>
                          <input
                            type="checkbox"
                            checked={index === coverImageIndex}
                            onChange={() => handleCheckCoverImage(index)}
                          />
                          <IconButton
                            onClick={() => removeSelectedImage(index)}
                          >
                            <DeleteIcon style={{ color: "red" }} />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  <IconButton onClick={() => handleChooseImage(null)}>
                    <AddIcon />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  sx={{
                    minWidth: { xs: 150, sm: 200 },
                    color: "#707070",
                    textTransform: "none",
                  }}
                  color="primary"
                  onClick={() => handleChooseImage(null)}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                  >
                    <IconButton aria-label="upload" sx={{ mb: 1, p: 0 }}>
                      <InsertPhotoIcon />
                    </IconButton>
                    <Typography variant="body2">Upload</Typography>
                  </Box>
                </Button>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Autocomplete
              value={productData.currency}
              disableClearable={!productData.currency}
              onChange={(e, newValue) =>
                handleInputChange("currency", newValue)
              }
              size="small"
              options={currencyTypes}
              renderInput={(params) => (
                <TextField
                  placeholder={"Currency"}
                  {...params}
                  variant="outlined"
                  sx={{ backgroundColor: "#FFFFFF" }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Autocomplete
              sx={{ backgroundColor: "#FFFFFF" }}
              value={productData.status}
              onChange={(event, newValue) =>
                handleInputChange("status", newValue)
              }
              options={["active", "draft"]}
              renderInput={(params) => (
                <TextField {...params} label="Status" size="small" fullWidth />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              sx={{ backgroundColor: "#FFFFFF" }}
              size="small"
              fullWidth
              label="Supplier"
              value={productData.vendor}
              onChange={(e) => handleInputChange("vendor", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6">Variants</Typography>
            {productData.variants.map((variant, index) => (
              <Card
                key={variant.uid}
                sx={{
                  mb: 2,
                  mt: 1,
                  border: "1px solid #D7D7D7",
                  backgroundColor: "#FFFFFF",
                }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography fontFamily="poppins" sx={{ fontWeight: 500 }}>
                      {variant.title || `Main SKU ${index + 1}`}
                      {(variant.isBundle || variant.isMaster) && (
                        <span style={{ color: "#DC585C" }}>
                          {` (Sub-SKU: ${variant.subSkus?.length})`}
                        </span>
                      )}
                    </Typography>
                    <Box>
                      <IconButton
                        onClick={() => handleRemoveVariant(index)}
                        disabled={productData.variants.length === 1}
                      >
                        <DeleteIcon
                          color={
                            productData.variants.length === 1
                              ? "disabled"
                              : "error"
                          }
                        />
                      </IconButton>
                      <IconButton onClick={() => handleToggleExpand(index)}>
                        {expandedVariants[index] ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </Box>
                  </Box>
                  {expandedVariants[index] && (
                    <>
                      <Divider />
                      <Grid container spacing={2} mt={1}>
                        <Grid item xs={12} sm={4}>
                          <Typography
                            fontFamily="poppins"
                            sx={{ fontWeight: 400, fontSize: 14 }}
                          >
                            Title <span style={{ color: "red" }}>*</span>
                          </Typography>
                          <TextField
                            sx={{ mt: 0.5, backgroundColor: "#FFFFFF" }}
                            size="small"
                            placeholder="Enter Title"
                            value={variant.title}
                            onChange={(e) =>
                              handleVariantChange(
                                index,
                                "title",
                                e.target.value
                              )
                            }
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography
                            fontFamily="poppins"
                            sx={{ fontWeight: 400, fontSize: 14 }}
                          >
                            SKU <span style={{ color: "red" }}>*</span>
                          </Typography>
                          <TextField
                            sx={{ mt: 0.5, backgroundColor: "#FFFFFF" }}
                            size="small"
                            placeholder="Enter SKU"
                            value={variant.sku}
                            onChange={(e) =>
                              handleVariantChange(index, "sku", e.target.value)
                            }
                            fullWidth
                            error={!!skuErrors[index]}
                            helperText={skuErrors[index] || ""}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography
                            fontFamily="poppins"
                            sx={{ fontWeight: 400, fontSize: 14 }}
                          >
                            HS Code
                          </Typography>
                          <TextField
                            sx={{ mt: 0.5, backgroundColor: "#FFFFFF" }}
                            size="small"
                            placeholder="Enter HS Code"
                            value={variant.hs_code}
                            onChange={(e) =>
                              handleVariantChange(
                                index,
                                "hs_code",
                                e.target.value
                              )
                            }
                            fullWidth
                            error={!!hsCodeErrors[index]}
                            helperText={hsCodeErrors[index] || ""}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography
                            fontFamily="poppins"
                            sx={{ fontWeight: 400, fontSize: 14 }}
                          >
                            HTS Code
                          </Typography>
                          <TextField
                            sx={{ mt: 0.5, backgroundColor: "#FFFFFF" }}
                            size="small"
                            placeholder="Enter HTS Code"
                            value={variant.hts_code}
                            onChange={(e) =>
                              handleVariantChange(
                                index,
                                "hts_code",
                                e.target.value
                              )
                            }
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography
                            fontFamily="poppins"
                            sx={{ fontWeight: 400, fontSize: 14 }}
                          >
                            Country of Origin
                          </Typography>
                          <TextField
                            sx={{ mt: 0.5, backgroundColor: "#FFFFFF" }}
                            size="small"
                            placeholder="Enter Country of Origin"
                            value={variant.country_of_origin}
                            onChange={(e) =>
                              handleVariantChange(
                                index,
                                "country_of_origin",
                                e.target.value
                              )
                            }
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography
                            fontFamily="poppins"
                            sx={{ fontWeight: 400, fontSize: 14 }}
                          >
                            Price (USD) <span style={{ color: "red" }}>*</span>
                          </Typography>
                          <TextField
                            sx={{ mt: 0.5, backgroundColor: "#FFFFFF" }}
                            type="number"
                            size="small"
                            placeholder="Enter Price"
                            value={variant.price}
                            onChange={(e) =>
                              handleVariantChange(
                                index,
                                "price",
                                e.target.value
                              )
                            }
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <DimensionsAndWeightInputWithUnit
                            dimensions={{
                              height: variant.height,
                              length: variant.length,
                              width: variant.width,
                            }}
                            weight={variant.weight}
                            onChange={(e) =>
                              onChangeDimensionAndWight(index, e.field, e.value)
                            }
                            dimensionUnitValue={variant.height_unit}
                            weightUnitValue={variant.weight_unit}
                          />
                        </Grid>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                            pl: 2,
                            pr: 2,
                          }}
                        >
                          <Box>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  disabled={variant.isMaster}
                                  checked={variant.isBundle}
                                  onChange={(e) =>
                                    handleBundleToggle(
                                      index,
                                      e.target.checked,
                                      "isBundle"
                                    )
                                  }
                                />
                              }
                              label="Bundle"
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  disabled={variant.isBundle}
                                  checked={variant.isMaster}
                                  onChange={(e) =>
                                    handleBundleToggle(
                                      index,
                                      e.target.checked,
                                      "isMaster"
                                    )
                                  }
                                />
                              }
                              label="Master"
                            />
                          </Box>
                          {(variant.isBundle || variant.isMaster) && (
                            <Button
                              variant="text"
                              onClick={() =>
                                handleAddSubSku(index, variant.isMaster)
                              }
                              sx={{
                                color: "#0038FF",
                                textTransform: "none",
                                fontFamily: "poppins",
                                fontSize: "14px",
                                fontWeight: 400,
                              }}
                            >
                              + Add Child Variant
                            </Button>
                          )}
                        </Box>
                        {(variant.isBundle || variant.isMaster) &&
                          variant.subSkus?.map((subSku, subIndex) => (
                            <Card
                              key={subIndex}
                              sx={{
                                m: 2,
                                p: 2,
                                border: "1px solid #E5E5E5",
                                backgroundColor: "#F7F8F9",
                                width: "100%",
                              }}
                            >
                              <Box
                                display="flex"
                                justifyContent="space-between"
                              >
                                <Typography fontWeight={500}>
                                  Sub-SKU {subIndex + 1}
                                </Typography>
                                <IconButton
                                  onClick={() =>
                                    handleRemoveSubSku(index, subIndex)
                                  }
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                              <Divider />
                              <Grid container spacing={2.5} mt={1}>
                                {variant.isBundle ? (
                                  <>
                                    <Grid item xs={12} sm={12}>
                                      <Typography
                                        fontFamily="poppins"
                                        sx={{ fontWeight: 400, fontSize: 14 }}
                                      >
                                        Select Product
                                      </Typography>
                                      <AutocompleteWithPagination
                                        backgroundColor="#FFF"
                                        customLabel="Product"
                                        selectedItem={subSku.product}
                                        onDataChange={(product) =>
                                          handleProductUpdate(
                                            index,
                                            subIndex,
                                            product
                                          )
                                        }
                                        width="100%"
                                        optionKey="name"
                                        apiEndPoint={(
                                          limit,
                                          skip,
                                          search,
                                          client
                                        ) => {
                                          const query = buildQuery({
                                            limit: limit,
                                            skip: skip,
                                          });
                                          return productService.fetch(
                                            client,
                                            search,
                                            query
                                          );
                                        }}
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                      <Typography
                                        fontFamily="poppins"
                                        sx={{ fontWeight: 400, fontSize: 14 }}
                                      >
                                        Select SKU
                                      </Typography>
                                      <AutocompleteWithPagination
                                        backgroundColor="#FFF"
                                        customLabel="SKU"
                                        selectedItem={subSku.sku}
                                        onDataChange={(selectedSku) =>
                                          updateSubSku(
                                            index,
                                            subIndex,
                                            subSku,
                                            selectedSku
                                          )
                                        }
                                        width="100%"
                                        optionKey="sku"
                                        apiEndPoint={(_, __, search) => {
                                          const product = subSku?.product;
                                          if (product?.variants?.length > 0) {
                                            const skus = product.variants
                                              .map((variant) => variant.sku)
                                              .filter(
                                                (sku) =>
                                                  sku &&
                                                  sku.includes(search || "")
                                              );
                                            return Promise.resolve({
                                              data: skus.map((sku) => ({
                                                sku,
                                              })),
                                              total: skus.length,
                                            });
                                          }
                                          return Promise.resolve({
                                            data: [],
                                            total: 0,
                                          });
                                        }}
                                      />
                                    </Grid>
                                  </>
                                ) : (
                                  <Grid item xs={12} sm={4}>
                                    <Typography
                                      fontFamily="poppins"
                                      sx={{ fontWeight: 400, fontSize: 14 }}
                                    >
                                      SKU{" "}
                                      <span style={{ color: "red" }}>*</span>
                                    </Typography>
                                    <TextField
                                      sx={{
                                        mt: 0.5,
                                        backgroundColor: "#FFFFFF",
                                      }}
                                      size="small"
                                      placeholder="Enter SKU"
                                      value={subSku.sku}
                                      onChange={(e) =>
                                        handleSubSkuChange(
                                          index,
                                          subIndex,
                                          "sku",
                                          e.target.value
                                        )
                                      }
                                      fullWidth
                                    />
                                  </Grid>
                                )}
                                <Grid item xs={12} sm={4}>
                                  <Typography
                                    fontFamily="poppins"
                                    sx={{ fontWeight: 400, fontSize: 14 }}
                                  >
                                    Title{" "}
                                    <span style={{ color: "red" }}>*</span>
                                  </Typography>
                                  <TextField
                                    sx={{ mt: 0.5, backgroundColor: "#FFFFFF" }}
                                    size="small"
                                    placeholder="Enter Title"
                                    value={subSku.title}
                                    onChange={(e) =>
                                      handleSubSkuChange(
                                        index,
                                        subIndex,
                                        "title",
                                        e.target.value
                                      )
                                    }
                                    fullWidth
                                  />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <Typography
                                    fontFamily="poppins"
                                    sx={{ fontWeight: 400, fontSize: 14 }}
                                  >
                                    Price (USD){" "}
                                    <span style={{ color: "red" }}>*</span>
                                  </Typography>
                                  <TextField
                                    sx={{ mt: 0.5, backgroundColor: "#FFFFFF" }}
                                    type="number"
                                    size="small"
                                    placeholder="Enter Price"
                                    value={subSku.price}
                                    onChange={(e) =>
                                      handleSubSkuChange(
                                        index,
                                        subIndex,
                                        "price",
                                        e.target.value
                                      )
                                    }
                                    fullWidth
                                  />
                                </Grid>
                                {!variant.isMaster && (
                                  <>
                                    <Grid item xs={12} sm={4}>
                                      <Typography
                                        fontFamily="poppins"
                                        sx={{ fontWeight: 400, fontSize: 14 }}
                                      >
                                        HS Code
                                      </Typography>
                                      <TextField
                                        sx={{
                                          mt: 0.5,
                                          backgroundColor: "#FFFFFF",
                                        }}
                                        size="small"
                                        placeholder="Enter HS Code"
                                        value={subSku.hs_code}
                                        onChange={(e) =>
                                          handleSubSkuChange(
                                            index,
                                            subIndex,
                                            "hs_code",
                                            e.target.value
                                          )
                                        }
                                        fullWidth
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                      <Typography
                                        fontFamily="poppins"
                                        sx={{ fontWeight: 400, fontSize: 14 }}
                                      >
                                        HTS Code
                                      </Typography>
                                      <TextField
                                        sx={{
                                          mt: 0.5,
                                          backgroundColor: "#FFFFFF",
                                        }}
                                        size="small"
                                        placeholder="Enter HTS Code"
                                        value={subSku.hts_code}
                                        onChange={(e) =>
                                          handleSubSkuChange(
                                            index,
                                            subIndex,
                                            "hts_code",
                                            e.target.value
                                          )
                                        }
                                        fullWidth
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                      <Typography
                                        fontFamily="poppins"
                                        sx={{ fontWeight: 400, fontSize: 14 }}
                                      >
                                        Country of Origin
                                      </Typography>
                                      <TextField
                                        sx={{
                                          mt: 0.5,
                                          backgroundColor: "#FFFFFF",
                                        }}
                                        size="small"
                                        placeholder="Enter Country of Origin"
                                        value={subSku.country_of_origin}
                                        onChange={(e) =>
                                          handleSubSkuChange(
                                            index,
                                            subIndex,
                                            "country_of_origin",
                                            e.target.value
                                          )
                                        }
                                        fullWidth
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                      <Typography
                                        fontFamily="poppins"
                                        sx={{ fontWeight: 400, fontSize: 14 }}
                                      >
                                        Inventory
                                      </Typography>
                                      <TextField
                                        sx={{
                                          mt: 0.5,
                                          backgroundColor: "#FFFFFF",
                                        }}
                                        type="number"
                                        size="small"
                                        placeholder="Enter Inventory"
                                        value={subSku.inventory}
                                        onChange={(e) =>
                                          handleSubSkuChange(
                                            index,
                                            subIndex,
                                            "inventory",
                                            e.target.value
                                          )
                                        }
                                        fullWidth
                                      />
                                    </Grid>
                                    <Grid item xs={12}>
                                      <DimensionsAndWeightInputWithUnit
                                        dimensions={{
                                          height: subSku.height,
                                          length: subSku.length,
                                          width: subSku.width,
                                        }}
                                        weight={subSku.weight}
                                        onChange={(e) =>
                                          onChangeSubDimensionAndWeight(
                                            index,
                                            subIndex,
                                            e.field,
                                            e.value
                                          )
                                        }
                                        dimensionUnitValue={subSku.height_unit}
                                        weightUnitValue={subSku.weight_unit}
                                      />
                                    </Grid>
                                  </>
                                )}
                              </Grid>
                            </Card>
                          ))}
                      </Grid>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
            <Button
              onClick={handleAddVariant}
              startIcon={<AddIcon />}
              sx={{ color: "#0038FF", textTransform: "none" }}
            >
              <Typography fontFamily="poppins" fontSize="14px" fontWeight={400}>
                Add more Variants
              </Typography>
            </Button>
          </Grid>
          <Grid
            sx={{
              display: "flex",
              justifyContent: "end",
              width: "100%",
              paddingBottom: 2,
              gap: 2,
            }}
            item
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{ textTransform: "none", border: "1px solid #DC585C" }}
            >
              <Typography
                fontFamily="poppins"
                fontSize="14px"
                fontWeight={500}
                color="#DC585C"
              >
                Cancel
              </Typography>
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                textTransform: "none",
                backgroundColor: validated ? "#DC585C" : "#B0B0B0",
              }}
              disabled={!validated}
            >
              <Typography
                fontFamily="poppins"
                fontSize="14px"
                fontWeight={500}
                color="#ffff"
              >
                {productId ? "Save" : "Add"}
              </Typography>
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ManageProduct;
